import { ClientMessage, ServerMessage, PlayerInput, MapId, KartId } from '@shared/types';
import { config } from '@shared/config';

type MessageHandler = (msg: ServerMessage) => void;

export class Network {
  private ws: WebSocket | null = null;
  private handlers: Map<string, Set<MessageHandler>> = new Map();
  private pingInterval: number | null = null;
  private lastPong = 0;
  latency = 0;

  /**
   * Connect to the game server.
   * action can be 'create', 'join', or 'solo'.
   */
  connect(
    action: string,
    name: string,
    opts?: { room?: string; kartId?: KartId; mapId?: MapId }
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      // Close any existing connection cleanly before opening a new one
      if (this.ws) {
        try {
          this.ws.onclose = null;
          this.ws.onerror = null;
          this.ws.onmessage = null;
          this.ws.close();
        } catch {
          // ignore
        }
        this.ws = null;
      }

      const protocol = location.protocol === 'https:' ? 'wss:' : 'ws:';
      // Connect directly to the game server on port 3010, bypassing the Vite proxy.
      // The Vite proxy is unstable for sustained WebSocket traffic from remote devices.
      const host = location.hostname + ':' + config.network.serverPort;

      let url = `${protocol}//${host}/ws?action=${encodeURIComponent(action)}&name=${encodeURIComponent(name)}`;
      if (opts?.room) url += `&room=${encodeURIComponent(opts.room)}`;
      if (opts?.kartId) url += `&kart=${encodeURIComponent(opts.kartId)}`;
      if (opts?.mapId) url += `&map=${encodeURIComponent(opts.mapId)}`;

      this.ws = new WebSocket(url);

      // Reject if connection takes too long
      const timeout = setTimeout(() => {
        reject(new Error('WebSocket connection timed out'));
        if (this.ws && this.ws.readyState !== WebSocket.OPEN) {
          this.ws.close();
          this.ws = null;
        }
      }, config.network.connectionTimeout);

      this.ws.onopen = () => {
        clearTimeout(timeout);
        this.startPing();
        resolve();
      };

      this.ws.onerror = () => {
        clearTimeout(timeout);
        reject(new Error('WebSocket connection failed'));
      };

      this.ws.onmessage = (e: MessageEvent) => {
        try {
          const msg = JSON.parse(e.data) as ServerMessage;
          if (msg.type === 'pong') {
            const pongT = (msg as { type: 'pong'; t: number }).t;
            this.latency = Date.now() - pongT;
            this.lastPong = Date.now();
            if (this.latency > 100) {
              console.warn(`[Network] High latency: ${this.latency}ms (sent t=${pongT}, now=${Date.now()})`);
            }
            return;
          }
          this.emit(msg.type, msg);
        } catch {
          // Silently ignore malformed messages
        }
      };

      this.ws.onclose = () => {
        this.stopPing();
        this.emit('_disconnected', { type: 'error', message: 'Connection lost' } as ServerMessage);
      };
    });
  }

  send(msg: ClientMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(msg));
    }
  }

  sendInput(seq: number, input: PlayerInput): void {
    this.send({ type: 'input', seq, input });
  }

  on(type: string, handler: MessageHandler): void {
    if (!this.handlers.has(type)) {
      this.handlers.set(type, new Set());
    }
    this.handlers.get(type)!.add(handler);
  }

  off(type: string, handler: MessageHandler): void {
    const set = this.handlers.get(type);
    if (set) {
      set.delete(handler);
      if (set.size === 0) {
        this.handlers.delete(type);
      }
    }
  }

  private emit(type: string, msg: ServerMessage): void {
    const set = this.handlers.get(type);
    if (set) {
      for (const handler of set) {
        try {
          handler(msg);
        } catch (err) {
          console.error(`[Network] Handler error for "${type}":`, err);
        }
      }
    }
  }

  private startPing(): void {
    this.stopPing();
    this.lastPong = Date.now();
    this.pingInterval = window.setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        const t = Date.now();
        this.ws.send(JSON.stringify({ type: 'ping', t }));
      }
    }, config.network.pingInterval);
  }

  private stopPing(): void {
    if (this.pingInterval !== null) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  close(): void {
    this.stopPing();
    if (this.ws) {
      // Prevent the onclose handler from firing disconnect events during intentional close
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      try {
        this.ws.close();
      } catch {
        // ignore
      }
      this.ws = null;
    }
    // Clear all handlers so stale listeners don't fire on reconnect
    this.handlers.clear();
    this.latency = 0;
  }

  get connected(): boolean {
    return this.ws !== null && this.ws.readyState === WebSocket.OPEN;
  }
}
